import { useMemo } from "react";
import {
    convertTour,
    convertPriceArray,
    convertPriceObject,
    convertPrice,
} from "@/utils/common/currencyConverter";
import { useCurrency } from "./useCurrency";
import { useExchangeRates } from "./useExchangeRates";

/*
Hook principale per la conversione client-side delle valute
useCurrency: valuta selezionata dall'utente
useExchangeRates: tassi di cambio dal backend
Singoli prezzi: convertPrice()
Oggetti con _eurPrices: convertPriceObject()
Array di oggetti: convertPriceArray()
Tour completi (con nested): convertTour()
Le funzioni di conversione si aggiornano automaticamente quando cambia la valuta
grazie alle dipendenze useMemo. Basta passare i dati con _eurPrices e verranno convertiti
automaticamente nella valuta corrente. 
*/

export const useCurrencyConversion = () => {
    const { currency } = useCurrency();
    const { rates, loading: ratesLoading } = useExchangeRates();

    /* Converte un prezzo singolo da EUR alla valuta corrente */
    const convertSinglePrice = useMemo(
        () =>
            (eurPrice: number | string): string => {
                // Se la valuta è EUR, non serve conversione
                if (currency === "EUR") {
                    const numPrice =
                        typeof eurPrice === "string"
                            ? parseFloat(eurPrice)
                            : eurPrice;
                    return Math.round(numPrice).toString();
                }

                // Se i tassi non sono ancora caricati o non disponibili, ritorna EUR
                if (!rates || !rates.rates) {
                    const numPrice =
                        typeof eurPrice === "string"
                            ? parseFloat(eurPrice)
                            : eurPrice;
                    // eslint-disable-next-line no-console
                    if (ratesLoading) {
                        console.warn(
                            "Exchange rates still loading, returning EUR price"
                        );
                    } else {
                        console.warn(
                            "Exchange rates not available, returning EUR price"
                        );
                    }
                    return Math.round(numPrice).toString();
                }

                return convertPrice(eurPrice, currency, rates.rates);
            },
        [currency, rates, ratesLoading]
    );

    /* Converte un oggetto con _eurPrices */
    const convertObject = useMemo(
        () =>
            <T extends Record<string, unknown>>(obj: T): T => {
                // eslint-disable-next-line no-underscore-dangle
                if (!rates || ratesLoading || !rates.rates || !obj._eurPrices) {
                    return obj;
                }
                return convertPriceObject(obj, currency, rates.rates);
            },
        [currency, rates, ratesLoading]
    );

    /* Converte un array di oggetti con _eurPrices */
    const convertArray = useMemo(
        () =>
            <T extends Record<string, unknown>>(array: T[]): T[] => {
                if (
                    !rates ||
                    ratesLoading ||
                    !rates.rates ||
                    !array ||
                    array.length === 0
                ) {
                    return array;
                }
                return convertPriceArray(array, currency, rates.rates);
            },
        [currency, rates, ratesLoading]
    );

    /* Converte un tour completo (con rooms, slots, tickets nested) */
    const convertTourData = useMemo(
        () =>
            <T extends Record<string, unknown>>(tour: T): T => {
                if (
                    !rates ||
                    ratesLoading ||
                    !rates.rates ||
                    // eslint-disable-next-line no-underscore-dangle
                    !tour._eurPrices
                ) {
                    return tour;
                }
                return convertTour(tour, currency, rates.rates) as T;
            },
        [currency, rates, ratesLoading]
    );

    return {
        convertPrice: convertSinglePrice,
        convertObject,
        convertArray,
        convertTour: convertTourData,
        ratesLoading,
        currency,
    };
};
